import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/ui/Header';
import OccupationSelector from './components/OccupationSelector';
import SenioritySelector from './components/SenioritySelector';
import AssessmentPreview from './components/AssessmentPreview';
import ProgressSteps from './components/ProgressSteps';
import ActionButtons from './components/ActionButtons';
import Icon from '../../components/AppIcon';
import Footer from '../../components/Footer';
import { useAuth } from '../../contexts/AuthContext';
import ComparisonChart from '../../components/ui/ComparisonChart';
import Button from '../../components/ui/Button';
import { loadAssessmentProgress, canUserStartAssessment } from '../../services/assessmentService';
import CreditService from '../../services/creditService';
import AdSense from '../../components/AdSense';
import CreditAvailabilityNotification from '../../components/CreditAvailabilityNotification';
import progressTrackingService from '../../services/progressTrackingService';


const CareerAssessmentSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, isAuthenticated, loading: authLoading, isPremium } = useAuth();
  const [selectedOccupation, setSelectedOccupation] = useState(null);
  const [selectedSeniority, setSelectedSeniority] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showComparisonChart, setShowComparisonChart] = useState(false);

  // Check if this is a "retake" flow
  const isRetake = location?.state?.isRetake || location?.search?.includes('retake=true');
  
  // Check if user is trying to retake but doesn't have permission
  useEffect(() => {
    if (isRetake && userProfile?.role === 'free') {
      // Check if they have a completed assessment
      const checkRetakePermission = async () => {
        try {
          const result = await loadAssessmentProgress(user.id);
          if (result.success && result.data && result.data.is_completed) {
            // Free user trying to retake completed assessment - block them
            navigate('/subscription-management', {
              state: {
                message: 'Retakes are only available for Premium users. Upgrade to retake your assessment and unlock unlimited career guidance.',
                showUpgrade: true
              }
            });
          }
        } catch (error) {
          console.error('Error checking retake permission:', error);
        }
      };
      
      if (user) {
        checkRetakePermission();
      }
    }
  }, [isRetake, userProfile?.role, user, navigate]);

  // Load existing assessment from database
  useEffect(() => {
    const loadExistingAssessment = async () => {
      if (!user || isRetake) {
        // Clear all previous assessment data for retake
        localStorage.removeItem('career-assessment-setup');
        localStorage.removeItem('current-assessment-config');
        localStorage.removeItem('saved-assessment-setup');
        console.log('Cleared previous assessment data for retake');
        return;
      }

      try {
        console.log('Checking for existing assessment for user:', user.id);
        const result = await loadAssessmentProgress(user.id);
        
        if (result.success && result.data && !result.data.is_completed) {
          console.log('Found existing incomplete assessment:', result.data);
          
          // If there's an existing incomplete assessment, redirect to continue it
          if (result.data.assessment_setup) {
            console.log('Redirecting to continue existing assessment...');
            navigate('/adaptive-questionnaire', {
              state: {
                assessmentSetup: result.data.assessment_setup,
                isRetake: false,
                continueExisting: true
              }
            });
            return;
          }
        } else if (result.success && result.data && result.data.is_completed) {
          console.log('Found completed assessment, checking credit eligibility...');
          
          // Check if user can start a new assessment using credit system
          const eligibilityCheck = await canUserStartAssessment(user?.id, userProfile?.role === 'premium');
          
          if (!eligibilityCheck.success) {
            console.error('Error checking assessment eligibility:', eligibilityCheck.error);
            setError('Failed to check assessment eligibility. Please try again.');
            return;
          }

          if (!eligibilityCheck.canStart) {
            console.log('User cannot start new assessment:', eligibilityCheck.reason);
            
            if (eligibilityCheck.reason === 'no_credits') {
              // Redirect to credits tab in subscription management
              navigate('/subscription-management?tab=credits', {
                state: {
                  message: 'You need to purchase credits to take additional assessments. Choose a credit pack or upgrade to Premium.',
                  showUpgrade: true
                }
              });
            } else {
              navigate('/subscription-management', {
                state: {
                  message: 'You cannot start a new assessment at this time.',
                  showUpgrade: true
                }
              });
            }
            return;
          }

          console.log('User can start new assessment:', eligibilityCheck.reason);
        } else {
          console.log('No existing assessment found, allowing new assessment');
        }
      } catch (error) {
        console.error('Error checking for existing assessment:', error);
        // If there's an error, allow them to start a new assessment
      }
    };

    loadExistingAssessment();
  }, [user, isRetake, navigate]);

  // Save progress to localStorage - but not during retake setup
  useEffect(() => {
    if (isRetake && (!selectedOccupation && !selectedSeniority)) {
      // Don't save progress during initial retake setup
      return;
    }

    if (selectedOccupation || selectedSeniority) {
      const progress = {
        occupation: selectedOccupation,
        seniority: selectedSeniority,
        timestamp: new Date()?.toISOString(),
        isRetake: isRetake
      };
      localStorage.setItem('career-assessment-setup', JSON.stringify(progress));
    }
  }, [selectedOccupation, selectedSeniority, isRetake]);

  const handleOccupationSelect = (occupation) => {
    console.log('=== OCCUPATION SELECTED ===');
    console.log('Selected occupation:', occupation);
    console.log('Occupation title:', occupation?.title);
    console.log('Occupation name:', occupation?.name);
    console.log('Occupation category:', occupation?.category);
    console.log('Occupation category_name:', occupation?.category_name);
    console.log('===========================');
    setSelectedOccupation(occupation);
  };

  const handleSenioritySelect = (seniority) => {
    setSelectedSeniority(seniority);
  };

  const handleStartAssessment = async () => {
    if (!selectedOccupation || !selectedSeniority) return;

    setIsLoading(true);
    
    try {
      // Debug: Log the selected occupation structure
      console.log('Selected occupation object:', selectedOccupation);
      console.log('Selected occupation category:', selectedOccupation?.category);
      
      // Simulate API call to save assessment setup
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Store assessment configuration with retake flag
      const assessmentConfig = {
        occupation: selectedOccupation,
        seniority: selectedSeniority,
        startedAt: new Date()?.toISOString(),
        userId: userProfile?.id || user?.id,
        isRetake: isRetake
      };
      
      localStorage.setItem('current-assessment-config', JSON.stringify(assessmentConfig));
      
      // Extract string values safely with more comprehensive checking
      const occupationTitle = selectedOccupation?.title || 
                             selectedOccupation?.name || 
                             (typeof selectedOccupation === 'string' ? selectedOccupation : null) ||
                             '';
      
      // Map seniority values to display labels
      const seniorityMapping = {
        'apprentice': 'Apprentice',
        'junior': 'Junior',
        'mid': 'Mid-Level',
        'senior': 'Senior',
        'executive': 'Executive'
      };
      
      const seniorityLevel = seniorityMapping[selectedSeniority] || selectedSeniority || 'Mid-Level';
      
      const industryName = selectedOccupation?.category_name || 
                          (selectedOccupation?.category?.name) || 
                          (typeof selectedOccupation?.category === 'string' ? selectedOccupation?.category : null) || 
                          'Technology';
      
      console.log('=== OCCUPATION DATA EXTRACTION ===');
      console.log('Raw selectedOccupation:', selectedOccupation);
      console.log('selectedOccupation.title:', selectedOccupation?.title);
      console.log('selectedOccupation.name:', selectedOccupation?.name);
      console.log('selectedOccupation.category:', selectedOccupation?.category);
      console.log('selectedOccupation.category_name:', selectedOccupation?.category_name);
      console.log('selectedSeniority (raw):', selectedSeniority);
      console.log('seniorityMapping result:', seniorityMapping[selectedSeniority]);
      console.log('Extracted occupationTitle:', occupationTitle);
      console.log('Extracted seniorityLevel:', seniorityLevel);
      console.log('Extracted industryName:', industryName);
      console.log('===================================');
      
      // Note: Assessment is NOT marked as completed here - only when questionnaire is finished
      // This is just the setup phase, not the completion
      
      // Navigate to adaptive questionnaire with proper state
      navigate('/adaptive-questionnaire', {
        state: {
          assessmentSetup: {
            occupation: occupationTitle,
            seniority: seniorityLevel,
            industry: industryName
          },
          isRetake: isRetake
        }
      });
    } catch (error) {
      console.error('Error starting assessment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveForLater = async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    setIsLoading(true);
    
    try {
      // Simulate API call to save progress
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const savedData = {
        occupation: selectedOccupation,
        seniority: selectedSeniority,
        savedAt: new Date()?.toISOString(),
        userId: userProfile?.id || user?.id
      };
      
      localStorage.setItem('saved-assessment-setup', JSON.stringify(savedData));
      
      // Show success message (you could add a toast notification here)
      alert('Progress saved successfully! You can continue later from your dashboard.');
      
    } catch (error) {
      console.error('Error saving progress:', error);
      alert('Error saving progress. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthRequired = () => {
    setShowAuthModal(true);
  };

  const handleLogout = () => {
    // Let AuthContext handle logout
    navigate('/authentication');
  };

  const handleUpgradeClick = () => {
    navigate('/subscription-management');
  };

  // Show loading state while authentication is loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Career Assessment Setup - CareerLift</title>
        <meta name="description" content="Set up your career assessment to receive personalized guidance and development plans tailored to your professional goals." />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <Header onAuthRequired={handleAuthRequired} onLogout={handleLogout} />
        
        <main className="flex-1 pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Page Header */}
            <div className="text-center mb-2">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Icon name="ClipboardList" size={32} className="text-primary" />
                <h1 className="text-3xl font-bold text-foreground">
                  {isRetake ? 'Retake Career Assessment' : 'Career Assessment Setup'}
                </h1>
              </div>
              <div className="flex justify-center mb-2">
                <audio
                  src="/assets/audio/Assessment.mp3"
                  controls
                  preload="metadata"
                  className="h-8 max-w-xs"
                />
              </div>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {isRetake 
                  ? 'Update your occupation and experience level to get a refreshed career development plan.' 
                  : 'Configure your personalized career evaluation by selecting your occupation and experience level. This helps us create targeted questions and recommendations for your professional development.'
                }
              </p>
              {isRetake && (
                <div className="mt-4 p-3 bg-primary/10 border border-primary/20 rounded-lg inline-block">
                  <p className="text-sm text-primary font-medium">
                    🔄 Assessment Retake - Your previous responses have been cleared
                  </p>
                </div>
              )}
            </div>

            {/* AdSense - visible for free users */}
            {!isPremium && (
              <div className="flex justify-center mb-1">
                <AdSense adSlot="1234567001" adFormat="auto" adStyle={{ display: 'block', minHeight: '15px', maxHeight: '25px' }} />
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-8">
                {/* Progress Steps */}
                <ProgressSteps currentStep={currentStep} />

                {/* Occupation Selection */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <OccupationSelector
                    selectedOccupation={selectedOccupation}
                    onOccupationSelect={handleOccupationSelect}
                    userRole={userProfile?.role || 'free'}
                    isAuthenticated={isAuthenticated}
                  />
                </div>

                {/* Seniority Selection */}
                {selectedOccupation && (
                  <div className="bg-card border border-border rounded-lg p-6">
                    <SenioritySelector
                      selectedSeniority={selectedSeniority}
                      onSenioritySelect={handleSenioritySelect}
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <ActionButtons
                  selectedOccupation={selectedOccupation}
                  selectedSeniority={selectedSeniority}
                  user={userProfile || user}
                  onSaveForLater={handleSaveForLater}
                  onStartAssessment={handleStartAssessment}
                />
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Assessment Preview */}
                <AssessmentPreview
                  selectedOccupation={selectedOccupation}
                  selectedSeniority={selectedSeniority}
                />

                {/* Credit Availability Notification */}
                {isAuthenticated && (
                  <CreditAvailabilityNotification
                    userId={user?.id}
                    isPremium={isPremium}
                    onUpgrade={() => navigate('/subscription-management', { state: { tab: 'credits' } })}
                  />
                )}

                {/* Plan Comparison Chart (show only for non-premium users) */}
                {!isPremium && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="GitCompare" size={20} className="text-primary" />
                      <h3 className="font-semibold text-foreground">Compare Plans</h3>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowComparisonChart(!showComparisonChart)}
                      iconName={showComparisonChart ? "ChevronUp" : "ChevronDown"}
                      iconPosition="right"
                    >
                      {showComparisonChart ? 'Hide' : 'Show'}
                    </Button>
                  </div>
                  
                  {!showComparisonChart ? (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">
                        See what's included with Free vs Premium subscriptions
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="text-center p-3 bg-muted/20 rounded-lg">
                          <Icon name="Star" size={16} className="text-muted-foreground mx-auto mb-1" />
                          <p className="text-xs font-medium text-foreground">Free Plan</p>
                          <p className="text-xs text-muted-foreground">Basic features</p>
                        </div>
                        <div className="text-center p-3 bg-primary/10 rounded-lg">
                          <Icon name="Crown" size={16} className="text-primary mx-auto mb-1" />
                          <p className="text-xs font-medium text-foreground">Premium Plan</p>
                          <p className="text-xs text-muted-foreground">Full access</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4">
                       <ComparisonChart
                        onUpgradeClick={handleUpgradeClick}
                        currentPlan={isPremium ? 'premium' : (userProfile?.role || 'free')}
                        showCTAButton={true}
                        className="text-xs"
                      />
                    </div>
                  )}
                </div>
                )}

                {/* Quick Tips */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Icon name="Lightbulb" size={20} className="text-warning" />
                    <h3 className="font-semibold text-foreground">Quick Tips</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-muted-foreground">
                        Choose the occupation that best matches your current role, even if it's not exact
                      </p>
                    </div>
                    <div className="flex items-start space-x-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-muted-foreground">
                        Select seniority based on your responsibilities, not just years of experience
                      </p>
                    </div>
                    <div className="flex items-start space-x-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-muted-foreground">
                        You can update these selections later in your profile settings
                      </p>
                    </div>
                  </div>
                </div>

                {/* Assessment Benefits */}
                <div className="bg-gradient-to-br from-primary/5 to-secondary/5 border border-primary/20 rounded-lg p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Icon name="Star" size={20} className="text-primary" />
                    <h3 className="font-semibold text-foreground">Why Take This Assessment?</h3>
                  </div>
                  <div className="space-y-2">
                    {[
                      'Get personalized career development recommendations',
                      'Identify skill gaps and growth opportunities',
                      'Receive weekly action items tailored to your role',
                      'Track your progress with industry-specific metrics',
                      'Access AI-powered insights for career advancement'
                    ]?.map((benefit, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <Icon name="ArrowRight" size={14} className="text-primary mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-muted-foreground">{benefit}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default CareerAssessmentSetup;